import React, { useCallback, useMemo, useRef } from 'react';

import { useParams, useSearchParams } from 'react-router';

import { Grid } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

import { GradeAutoComplete, MesFaturamentoAutoComplete, StoreAutoComplete } from 'components/autocompletes';
import { CissTextField, Editor } from 'components/fields';
import { Row } from 'components/form';
import { Block, FormPage } from 'components/page';
import { SubTitle } from 'components/text';
import { useFormContext } from 'context/FormContext';
import { useFormMutation } from 'mutations/useFormMutation';
import { RequestGradeModelProps } from 'pages/logistica/grade/types';

const getDateIsoString = (date?: Date | string | null): string => {
    let dateNow = new Date();

    if (date && typeof date === 'string') {
        dateNow = new Date(date);
    } else if (date && date !== 'string') {
        dateNow = date as Date;
    }

    return dateNow.toISOString().replace(/\.\d{3}Z$/, '');
};

export function GradeFormComp() {
    const urlSave = useRef('/gestao/grade/save');
    const urlUpdate = useRef('/gestao/grade/');
    const [searchParams] = useSearchParams();
    const idLoja = searchParams.get('idLoja');
    const idTipoGrade = searchParams.get('idTipoGrade');

    const { mutate } = useFormMutation();
    const { setContent, setLoading } = useFormContext();
    const { id } = useParams();

    const validationNewGrade = useMemo(
        () =>
            Yup.object({
                loja: Yup.object().required(),
                tipoGrade: Yup.object().required(),
                mesFaturamento: Yup.string().required(),
                dtColeta: Yup.string().required(),
                dtEntrega: Yup.string().required(),
                dtLimitePedido: Yup.string().required(),
                dhInicioEntrega: Yup.string().required(),
                dhFimEntrega: Yup.string().required(),
                dsObservacao: Yup.string().nullable(),
            }),
        [],
    );

    const initialValues: RequestGradeModelProps = useMemo(
        () => ({
            dtColeta: getDateIsoString(),
            dtEntrega: getDateIsoString(),
            dtLimitePedido: getDateIsoString(),
            dsObservacao: '',
            loja: idLoja ? JSON.parse(idLoja) : null,
            tipoGrade: idTipoGrade ? JSON.parse(idTipoGrade) : null,
        }),
        [idLoja, idTipoGrade],
    );

    const onSubmitGrade = (values: RequestGradeModelProps, formik: FormikHelpers<RequestGradeModelProps>): void => {
        setLoading(true);

        if (values.dhInicioEntrega && typeof values.dhInicioEntrega !== 'string') {
            const dhInicioEntrega = new Date(values.dhInicioEntrega);

            values.dhInicioEntrega = format(dhInicioEntrega, "yyyy-MM-dd'T'HH:mm:ss");
        }

        if (values.dhFimEntrega && typeof values.dhFimEntrega !== 'string') {
            const dhFimEntrega = new Date(values.dhFimEntrega);

            values.dhFimEntrega = format(dhFimEntrega, "yyyy-MM-dd'T'HH:mm:ss");
        }

        mutate(
            {
                url: id ? `${urlUpdate.current}${id}` : urlSave.current,
                method: id ? 'PUT' : 'POST',
                formData: id ? values : [values],
                formik,
            },
            {
                onSuccess: (response: any) => {
                    const { data } = response?.data || {};

                    setContent(data);
                },
                onSettled: () => {
                    setLoading(false);
                },
            },
        );
    };

    const mapContent = useCallback((content: RequestGradeModelProps) => {
        return {
            ...content,
            dtLimitePedido: getDateIsoString(content.dtLimitePedido),
            dtFaturamento: getDateIsoString(content.dtFaturamento),
            dtEntrega: getDateIsoString(content.dtEntrega),
            dtColeta: getDateIsoString(content.dtColeta),
        };
    }, []);

    return (
        <FormPage
            title={'Grade'}
            values={initialValues}
            onSubmit={onSubmitGrade}
            validationSchema={validationNewGrade}
            url="/gestao/grade/"
            mapContentToInitialValues={mapContent}
        >
            {(formik: FormikProps<RequestGradeModelProps>) => (
                <React.Fragment>
                    <Block>
                        <Row>
                            <Grid sx={{ flex: 1 }}>
                                <StoreAutoComplete
                                    name="loja"
                                    fgTipoLoja
                                    multiple={false}
                                    defaultStore={!id && !idLoja}
                                    value={formik.values.loja || null}
                                    error={formik.errors.loja}
                                    onChange={(e, value) => {
                                        formik.setFieldValue('loja', value);
                                    }}
                                />
                            </Grid>

                            <Grid sx={{ flex: 1 }}>
                                <GradeAutoComplete
                                    name="tipoGrade"
                                    multiple={false}
                                    value={formik.values.tipoGrade || null}
                                    error={formik.errors.tipoGrade}
                                    onChange={(e, value) => {
                                        formik.setFieldValue('tipoGrade', value);
                                    }}
                                />
                            </Grid>

                            <Grid sx={{ flex: 1 }}>
                                <MesFaturamentoAutoComplete
                                    value={formik.values.mesFaturamento || null}
                                    error={formik.errors.mesFaturamento}
                                    onChange={(e, value) => formik.setFieldValue('mesFaturamento', value)}
                                />
                            </Grid>
                        </Row>

                        <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                            <Row>
                                <DatePicker
                                    sx={{ flex: 1 }}
                                    format="dd/MM/yyyy"
                                    value={formik.values.dtLimitePedido ? new Date(formik.values.dtLimitePedido) : null}
                                    onChange={(value) => {
                                        formik.setFieldValue('dtLimitePedido', value?.toISOString());
                                    }}
                                    slotProps={{
                                        textField: {
                                            name: 'dtLimitePedido',
                                            label: 'Data limite do pedido',
                                            error: Boolean(formik.errors.dtLimitePedido),
                                            helperText: formik.errors.dtLimitePedido,
                                        },
                                    }}
                                />

                                <DatePicker
                                    sx={{ flex: 1 }}
                                    format="dd/MM/yyyy"
                                    value={formik.values.dtColeta ? new Date(formik.values.dtColeta) : null}
                                    onChange={(value) => {
                                        formik.setFieldValue('dtColeta', value?.toISOString());
                                    }}
                                    slotProps={{
                                        textField: {
                                            name: 'dtColeta',
                                            label: 'Data de coleta',
                                            error: Boolean(formik.errors.dtColeta),
                                            helperText: formik.errors.dtColeta,
                                        },
                                    }}
                                />

                                <DatePicker
                                    sx={{ flex: 1 }}
                                    format="dd/MM/yyyy"
                                    value={formik.values.dtEntrega ? new Date(formik.values.dtEntrega) : null}
                                    onChange={(value) => {
                                        formik.setFieldValue('dtEntrega', value?.toISOString());
                                    }}
                                    slotProps={{
                                        textField: {
                                            name: 'dtEntrega',
                                            label: 'Data de entrega',
                                            error: Boolean(formik.errors.dtEntrega),
                                            helperText: formik.errors.dtEntrega,
                                        },
                                    }}
                                />
                            </Row>

                            <Row>
                                <TimePicker
                                    sx={{ flex: 1 }}
                                    value={formik.values.dhInicioEntrega ? new Date(formik.values.dhInicioEntrega) : null}
                                    onChange={(value) => formik.setFieldValue('dhInicioEntrega', value?.toISOString())}
                                    slotProps={{
                                        textField: {
                                            name: 'dhInicioEntrega',
                                            label: 'Hora de início da entrega',
                                            error: Boolean(formik.errors.dhInicioEntrega),
                                            helperText: formik.errors.dhInicioEntrega,
                                        },
                                    }}
                                />

                                <TimePicker
                                    sx={{ flex: 1 }}
                                    value={formik.values.dhFimEntrega ? new Date(formik.values.dhFimEntrega) : null}
                                    onChange={(value) => formik.setFieldValue('dhFimEntrega', value?.toISOString())}
                                    slotProps={{
                                        textField: {
                                            name: 'dhFimEntrega',
                                            label: 'Hora de fim da entrega',
                                            error: Boolean(formik.errors.dhFimEntrega),
                                            helperText: formik.errors.dhFimEntrega,
                                        },
                                    }}
                                />
                            </Row>
                        </LocalizationProvider>
                    </Block>

                    <Block>
                        <SubTitle label={'Observação'} />

                        <Grid size={12}>
                            <Editor value={formik.values.dsObservacao} onChangeText={(text: string) => formik.setFieldValue('dsObservacao', text)} />
                        </Grid>
                    </Block>
                </React.Fragment>
            )}
        </FormPage>
    );
}
